import React from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy, ExternalLink, FileText, Globe, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * View-Document dialog. Extracted from ChatbotDetailPage.jsx (iter132.2).
 * The parent supplies a status→color helper and the delete request (the
 * dialog just asks for confirmation before invoking it, via ``onRequestDelete``
 * — the actual confirm modal lives in DeleteConfirmDialog.jsx).
 */
export const ViewDocumentDialog = ({
  open,
  onOpenChange,
  document,
  getStatusColor,
  onRequestDelete,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-white border-slate-200 max-w-3xl max-h-[85vh] flex flex-col">
      <DialogHeader>
        <DialogTitle className="text-slate-900 flex items-center gap-2">
          {document?.source_type === 'url'
            ? <Globe className="w-5 h-5 text-blue-600" />
            : <FileText className="w-5 h-5 text-slate-500" />}
          {document?.name}
        </DialogTitle>
      </DialogHeader>
      <div className="flex-1 overflow-hidden py-4">
        <div className="space-y-3 h-full flex flex-col">
          <div className="flex flex-wrap gap-2 text-xs items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <Badge className={getStatusColor(document?.status)}>{document?.status}</Badge>
              <Badge className="bg-slate-100 text-slate-600">{document?.document_type}</Badge>
              {document?.word_count && (
                <Badge className="bg-slate-100 text-slate-600">
                  {document?.word_count.toLocaleString()} words
                </Badge>
              )}
              {document?.created_at && (
                <Badge className="bg-slate-100 text-slate-600">
                  Added: {new Date(document.created_at).toLocaleDateString()}
                </Badge>
              )}
            </div>
            {document?.content && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(document.content);
                  toast.success('Content copied to clipboard');
                }}
                className="border-slate-300 text-slate-600 text-xs"
                data-testid="doc-copy-btn"
              >
                <Copy className="w-3 h-3 mr-1" />
                Copy
              </Button>
            )}
          </div>
          {document?.source_url && (
            <div className="text-sm text-blue-600">
              <a
                href={document.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                {document.source_url}
              </a>
            </div>
          )}
          <div
            className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex-1 overflow-y-auto min-h-[200px] max-h-[50vh]"
            data-testid="doc-preview-container"
          >
            {document?.previewUrl ? (
              <iframe
                src={document.previewUrl}
                title={document?.name || 'Document preview'}
                className="w-full h-full min-h-[400px] rounded border-0 bg-white"
                data-testid="doc-preview-iframe"
              />
            ) : document?.content ? (
              <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                {document.content}
              </pre>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500">
                <div className="text-center">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-slate-400" />
                  <p>Content not available for preview</p>
                  <p className="text-xs mt-1">The document may still be processing</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          className="border-slate-300 text-slate-700"
        >
          Close
        </Button>
        <Button
          variant="destructive"
          onClick={() => onRequestDelete?.(document)}
          className="bg-red-600 hover:bg-red-700"
          data-testid="doc-delete-btn"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default ViewDocumentDialog;
